/**
 * Intent v1 — slot registry.
 *
 * The registry is the single source of truth for: which slots exist, which playbook
 * section each one lives under, the markdown label used in the document, and the
 * non-engineer question a human must answer when the slot is `unknown`.
 *
 * ## Superset, not per-kind registries
 *
 * `SLOT_REGISTRY` is a *superset across every {@link IntentKind}*: it defines every
 * slot that exists for any kind, and it stays total and flat. Three consumers depend
 * on that shape and would break if the registry were split into parallel per-kind
 * registries — the parser's label reverse-index ({@link slotIdForLabel}), the web
 * label helpers in `packages/web/src/lib/intent-labels.ts`, and `questionForSlot` in
 * `render.ts` all look a slot up by id with no kind in hand.
 *
 * What *is* kind-dependent is which slots a reporter owes, and that lives in
 * {@link KIND_SLOT_SETS}. "Which slots exist" and "which slots this reporter owes"
 * are deliberately separate questions.
 *
 * ## Labels are globally unique
 *
 * {@link slotIdForLabel} is one flat, case-insensitive map over the whole registry, so
 * two slots sharing a label would silently mis-map a section rather than fail loudly.
 * The feature kind's `currentWorkaround` is therefore labelled "Current workaround",
 * distinct from the bug kind's "Workaround". Label uniqueness is asserted by test.
 *
 * ## Required / optional
 *
 * Optional slots are optional because QA and customer reporters often cannot name a
 * direction or a success metric, and forcing an answer fabricates content on a
 * team-visible record. They must still be answered *or explicitly marked unknown*
 * before an intent counts as complete.
 */
import { PLAYBOOK_SECTIONS, PLAYBOOK_SECTION_TITLES, SLOT_IDS } from "./types.js";
/**
 * The registry, keyed by slot id. Typed as a total record so a slot id without a
 * definition (or a definition without an id) fails typecheck.
 *
 * Entries are listed in {@link SLOT_IDS} order, grouped by playbook section.
 */
export const SLOT_REGISTRY = {
    title: {
        id: "title",
        section: "problem",
        label: "Title",
        required: true,
        question: "In one sentence, what would you call this problem if you were telling a colleague about it?",
    },
    symptom: {
        id: "symptom",
        section: "problem",
        label: "Symptom",
        required: true,
        question: "What did you see happen? Describe it as if the person reading has never used this part of the product.",
    },
    reproSteps: {
        id: "reproSteps",
        section: "problem",
        label: "Steps to reproduce",
        required: true,
        question: "Walk me through exactly what you clicked or typed, step by step, right up to the moment it went wrong.",
    },
    expectedVsActual: {
        id: "expectedVsActual",
        section: "problem",
        label: "Expected vs actual",
        required: true,
        question: "What did you expect to happen, and what happened instead?",
    },
    question: {
        id: "question",
        section: "problem",
        label: "The question",
        required: false,
        question: "What is it you need to know? Ask it in one sentence, the way you would ask a colleague standing next to you.",
    },
    questionContext: {
        id: "questionContext",
        section: "problem",
        label: "Context",
        required: false,
        question: "What were you doing or trying to decide when this came up? Tell me anything you have already looked at or been told.",
    },
    desiredOutcome: {
        id: "desiredOutcome",
        section: "proposedOutcome",
        label: "Desired outcome",
        required: false,
        question: "What do you want to be able to do that you cannot do today? Describe the result you want, not how it should be built.",
    },
    proposedDirection: {
        id: "proposedDirection",
        section: "proposedOutcome",
        label: "Proposed direction",
        required: false,
        question: "If you already have an idea of how this should work instead, describe it. It is completely fine to say you do not know.",
    },
    successSignal: {
        id: "successSignal",
        section: "proposedOutcome",
        label: "Success signal",
        required: false,
        question: "How would you know this was fixed? What would you check, and what would you expect to see?",
    },
    usersAffected: {
        id: "usersAffected",
        section: "affectedUsersAndSystems",
        label: "Users affected",
        required: true,
        question: "Who else is hitting this — just you, your team, or customers? Roughly how many people?",
    },
    frequency: {
        id: "frequency",
        section: "affectedUsersAndSystems",
        label: "Frequency",
        required: true,
        question: "Does this happen every time, or only sometimes? If sometimes, under what conditions?",
    },
    environment: {
        id: "environment",
        section: "affectedUsersAndSystems",
        label: "Environment",
        required: true,
        question: "Where were you when this happened — which site or environment, which browser or app, and roughly when?",
    },
    whoBenefits: {
        id: "whoBenefits",
        section: "affectedUsersAndSystems",
        label: "Who benefits",
        required: false,
        question: "Who would actually use this, and what does it let them get done? Just you, your team, or customers?",
    },
    answerAudience: {
        id: "answerAudience",
        section: "affectedUsersAndSystems",
        label: "Who needs the answer",
        required: false,
        question: "Besides you, who is waiting on this answer, and what will they do once they have it?",
    },
    severity: {
        id: "severity",
        section: "constraints",
        label: "Severity",
        required: true,
        question: "How badly does this hurt right now? Can you still get your work done, or are you completely stuck?",
    },
    reproducibility: {
        id: "reproducibility",
        section: "constraints",
        label: "Reproducibility",
        required: true,
        question: "If you try the exact same thing again, does it fail again? Every time, or only sometimes?",
    },
    workaround: {
        id: "workaround",
        section: "constraints",
        label: "Workaround",
        required: true,
        question: "Is there anything you can do to get around it for now, even if it is awkward? If there is nothing, say so.",
    },
    currentWorkaround: {
        id: "currentWorkaround",
        section: "constraints",
        label: "Current workaround",
        required: false,
        question: "How do you handle this today without it? If the honest answer is that you simply go without, say that.",
    },
    answerByWhen: {
        id: "answerByWhen",
        section: "constraints",
        label: "Needed by",
        required: false,
        question: "When do you need the answer by, and what happens if it arrives later than that?",
    },
    boundary: {
        id: "boundary",
        section: "constraints",
        label: "Boundary (out of scope)",
        required: true,
        question: "Is there anything that looks related but you are sure is NOT part of this problem?",
    },
};
/** Every slot definition, in playbook order. */
export const SLOTS_IN_ORDER = SLOT_IDS.map((id) => SLOT_REGISTRY[id]);
/**
 * Which slots each kind's reporter owes. This is the completion authority — the
 * per-slot `required` flag on {@link SlotDefinition} is not.
 *
 * A slot id absent from both lists for a kind simply does not apply to that kind: it
 * is never asked for, never rendered, and never counted against completion.
 *
 * The `bug` entry reproduces the pre-kind behaviour exactly — the same 11 required and
 * 2 optional ids, in the same order — because the bug path is the reference behaviour
 * every other kind is measured against and must not move.
 *
 * Lists are written in {@link SLOT_IDS} order. The selectors below re-derive that
 * order by filtering `SLOT_IDS`, so membership here is authoritative and ordering here
 * is only a readability convention.
 */
export const KIND_SLOT_SETS = {
    bug: {
        required: [
            "title",
            "symptom",
            "reproSteps",
            "expectedVsActual",
            "usersAffected",
            "frequency",
            "environment",
            "severity",
            "reproducibility",
            "workaround",
            "boundary",
        ],
        optional: ["proposedDirection", "successSignal"],
    },
    feature: {
        required: ["title", "desiredOutcome", "successSignal", "whoBenefits", "currentWorkaround"],
        optional: ["proposedDirection", "boundary"],
    },
    inquiry: {
        required: ["title", "question", "questionContext", "answerAudience", "answerByWhen"],
        optional: ["boundary"],
    },
};
// ─── Kind-scoped selectors ──────────────────────────────────────────
//
// These are the selectors to reach for. Every one of them derives its order by
// filtering SLOT_IDS, so a caller always gets playbook order regardless of how the
// membership lists in KIND_SLOT_SETS happen to be written.
/**
 * Every slot id belonging to `kind`, required and optional together, in playbook order.
 *
 * @param kind The intent kind.
 * @returns The kind's slot ids — the complete set of slots that apply to it.
 */
export function slotIdsForKind(kind) {
    const set = KIND_SLOT_SETS[kind];
    const members = new Set([...set.required, ...set.optional]);
    return SLOT_IDS.filter((id) => members.has(id));
}
/**
 * Ids of the slots that must be *filled* for an intent of `kind` to be complete.
 *
 * @param kind The intent kind.
 * @returns The kind's required slot ids, in playbook order.
 */
export function requiredSlotIdsForKind(kind) {
    const required = new Set(KIND_SLOT_SETS[kind].required);
    return SLOT_IDS.filter((id) => required.has(id));
}
/**
 * Ids of the slots that may be filled *or explicitly marked unknown* for an intent of
 * `kind` to be complete.
 *
 * @param kind The intent kind.
 * @returns The kind's optional slot ids, in playbook order.
 */
export function optionalSlotIdsForKind(kind) {
    const optional = new Set(KIND_SLOT_SETS[kind].optional);
    return SLOT_IDS.filter((id) => optional.has(id));
}
/**
 * Slot definitions belonging to one playbook section *and* one kind, in playbook order.
 *
 * `openQuestions` owns no slots of its own for any kind — it is derived from unknown
 * slots — so it always returns an empty list.
 *
 * @param kind The intent kind.
 * @param section The playbook section.
 * @returns The definitions to render under that heading for that kind.
 */
export function slotsForSectionAndKind(kind, section) {
    return slotIdsForKind(kind)
        .filter((id) => SLOT_REGISTRY[id].section === section)
        .map((id) => SLOT_REGISTRY[id]);
}
/**
 * Whether `slotId` applies to `kind` at all. A slot outside the kind is never asked
 * for, never rendered, and never counted against completion.
 *
 * @param kind The intent kind.
 * @param slotId The slot id to test.
 * @returns True when the slot belongs to the kind's required or optional set.
 */
export function isSlotInKind(kind, slotId) {
    const set = KIND_SLOT_SETS[kind];
    return set.required.includes(slotId) || set.optional.includes(slotId);
}
// ─── Global (pre-kind) selectors, kept for back-compat ───────────────
//
// These describe the *bug* kind, which is what they have always described: before
// kinds existed there was one registry and it was bug-shaped. They are pinned to
// KIND_SLOT_SETS.bug rather than derived from the deprecated per-slot `required` flag
// so that adding a non-bug slot to the registry can never change their value.
/**
 * Ids of the slots that must be filled for an intent to be complete.
 *
 * **Superseded:** describes the bug kind only. Prefer {@link requiredSlotIdsForKind}.
 */
export const REQUIRED_SLOT_IDS = requiredSlotIdsForKind("bug");
/**
 * Ids of the slots that may be filled or explicitly marked unknown.
 *
 * **Superseded:** describes the bug kind only. Prefer {@link optionalSlotIdsForKind}.
 */
export const OPTIONAL_SLOT_IDS = optionalSlotIdsForKind("bug");
/**
 * Slot definitions belonging to one playbook section, in registry order, across *every*
 * kind.
 *
 * **Superseded:** this is a non-kind-scoped compatibility selector, kept for callers
 * that predate intent kinds and genuinely want the superset. It is no longer used to
 * render a document — `render.ts` calls {@link slotsForSectionAndKind} — and any caller
 * working with a single intent should do the same, or it will walk slots that the
 * intent's kind does not own.
 */
export function slotsForSection(section) {
    return SLOTS_IN_ORDER.filter((slot) => slot.section === section);
}
/** Reverse index from lower-cased label to slot id, used by the parser. */
const LABEL_TO_SLOT_ID = new Map(SLOTS_IN_ORDER.map((slot) => [slot.label.toLowerCase(), slot.id]));
/**
 * Resolve a level-3 markdown label back to a slot id. Returns `undefined` for an
 * unrecognised label — callers must treat that as a warning, never as a fill.
 */
export function slotIdForLabel(label) {
    return LABEL_TO_SLOT_ID.get(label.trim().toLowerCase());
}
/** Reverse index from lower-cased section title to section id, used by the parser. */
const TITLE_TO_SECTION = new Map(PLAYBOOK_SECTIONS.map((section) => [PLAYBOOK_SECTION_TITLES[section].toLowerCase(), section]));
/** Resolve a level-2 markdown heading back to a playbook section id. */
export function sectionForTitle(title) {
    return TITLE_TO_SECTION.get(title.trim().toLowerCase());
}
//# sourceMappingURL=slots.js.map